
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RepaymentsTable } from './repayments/RepaymentsTable';
import { sampleRepayments } from './repayments/data/sampleRepayments';
import { formatCurrency } from './repayments/utils/formatters';
import { PaymentDetailsDialog } from './repayments/PaymentDetailsDialog';
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from '@/hooks/use-toast';

export const RepaymentActivity = () => {
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRepayment, setSelectedRepayment] = useState<number | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  // Calculate summary metrics
  const totalOutstanding = sampleRepayments
    .filter(r => r.status === "Upcoming")
    .reduce((sum, r) => sum + r.amount, 0);

  const upcomingRepayments = sampleRepayments
    .filter(r => r.status === "Upcoming" && r.daysToGo <= 30);
  
  const upcomingTotal = upcomingRepayments.reduce((sum, r) => sum + r.amount, 0);

  // Filter repayments based on search and status
  const filteredRepayments = sampleRepayments.filter(repayment => {
    // Status filter
    if (filterStatus !== "all" && repayment.status !== filterStatus) {
      return false;
    }
    
    // Search query
    if (searchQuery && !repayment.borrowing.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Sort repayments: Overdue first, then upcoming by days to go, then paid
  const sortedRepayments = [...filteredRepayments].sort((a, b) => {
    // First by status (Overdue > Upcoming > Paid)
    if (a.status !== b.status) {
      if (a.status === "Overdue") return -1;
      if (b.status === "Overdue") return 1;
      if (a.status === "Upcoming") return -1;
      if (b.status === "Upcoming") return 1;
    }
    
    // For upcoming payments, sort by days to go (ascending)
    if (a.status === "Upcoming" && b.status === "Upcoming") {
      return (a.daysToGo || 0) - (b.daysToGo || 0);
    }
    
    // For paid payments, sort by date (descending)
    if (a.status === "Paid" && b.status === "Paid") {
      return new Date(b.paidDate || b.date).getTime() - 
             new Date(a.paidDate || a.date).getTime();
    }
    
    return 0;
  });

  const handleViewDetails = (repayment: any) => {
    setSelectedRepayment(repayment.id);
    setShowPaymentDialog(true);
  };

  const handleProcessPayment = (repayment: any) => {
    setSelectedRepayment(repayment.id);
    setShowPaymentDialog(true);
  };

  return (
    <div className="space-y-6">
      <Card className="shadow-md border-0">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl">Repayment Management</CardTitle>
              <CardDescription>
                Manage all borrowing repayments
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Total Outstanding
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 px-4 pb-3">
                <div className="text-2xl font-bold">
                  {formatCurrency(totalOutstanding)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Across {sampleRepayments.filter(r => r.status === "Upcoming").length} payments
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Due in 30 Days
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 px-4 pb-3">
                <div className="text-2xl font-bold">
                  {formatCurrency(upcomingTotal)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {upcomingRepayments.length} payments upcoming
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Processed This Month
                </CardTitle>
              </CardHeader>
              <CardContent className="py-0 px-4 pb-3">
                <div className="text-2xl font-bold">
                  {formatCurrency(sampleRepayments
                    .filter(r => r.status === "Paid" && new Date(r.paidDate || r.date).getMonth() === new Date().getMonth())
                    .reduce((sum, r) => sum + r.amount, 0))}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {sampleRepayments.filter(r => r.status === "Paid" && 
                    new Date(r.paidDate || r.date).getMonth() === new Date().getMonth()).length} payments
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Input
              placeholder="Search borrowings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="md:w-1/3"
            />
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="md:w-1/5">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Upcoming">Upcoming</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex-1 flex justify-end gap-2">
              <Button variant="outline" size="sm">
                Export Data
              </Button>
              <Button size="sm">
                Process Batch Payments
              </Button>
            </div>
          </div>
          
          {/* Repayments Table */}
          <RepaymentsTable 
            repayments={sortedRepayments}
            onViewDetails={handleViewDetails}
            onProcessPayment={handleProcessPayment}
          />
          
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {sortedRepayments.length} of {sampleRepayments.length} repayments
          </div>
        </CardContent>
      </Card>
      
      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        open={showPaymentDialog}
        onOpenChange={setShowPaymentDialog}
        repayment={selectedRepayment ? sampleRepayments.find(r => r.id === selectedRepayment) : null}
      />
    </div>
  );
};
